import { startOfDateIn } from "./events";
import type { Window } from "./recurrence";

/**
 * The month grid, as calendar dates.
 *
 * **Calendar dates in, calendar dates out, and no zone anywhere** — with one
 * deliberate exception at the bottom. A grid cell is `YYYY-MM-DD`: the 20th of
 * August is the 20th of August on every phone in the family, and constructing
 * an instant to hold one is the move that slides a cell by a day for somebody.
 * This is the same reasoning that keeps an all-day event out of the recurrence
 * machinery in `recurrence.ts`.
 *
 * The exception is `monthWindow`, which turns the grid into the instants
 * `expandEvents` is bounded by. That conversion is exactly where a zone
 * belongs, and it is the only place here that takes one.
 */

/**
 * Six weeks, always, whatever the month needs.
 *
 * A month spans four to six weeks, so sizing the grid to fit would change its
 * height as you page through the year — and the selected day's list sits
 * directly underneath it, so the answer a person opened the app for would jump
 * every time they moved a month. A fixed six rows is 264px of grid that never
 * moves.
 */
const GRID_DAYS = 42;

const DAY = 86_400_000;

/**
 * Whether a value off the URL names a real day.
 *
 * **Round-tripped, not just parsed.** `Date.parse("2026-02-30T00:00:00Z")`
 * does not fail — it rolls forward and returns 2 March. Parsing alone would let
 * a day that does not exist through wearing another day's identity, and the
 * grid would then highlight a cell whose date is not the one in the address
 * bar.
 */
export function isCalendarDate(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const at = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(at) && new Date(at).toISOString().slice(0, 10) === value;
}

/**
 * A calendar date as the `Date` the formatters in `format.ts` take.
 *
 * **Every caller must pass `"UTC"` as the zone alongside it.** The date is
 * carried as UTC midnight, which is the same shape `startAnchor` gives an
 * all-day event, and reading it in any other zone is what moves it a day.
 */
export const dateAsUtc = (date: string) => new Date(`${date}T00:00:00Z`);

/** The month a date belongs to, `YYYY-MM`. The grid's identity. */
export const monthOf = (date: string) => date.slice(0, 7);

/** Whole days on, or back. UTC has no DST, so this is exact arithmetic. */
export const addDays = (date: string, days: number) =>
  new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY).toISOString().slice(0, 10);

/**
 * The same day of the month, `months` away, **clamped to a day that exists** —
 * 31 January forward a month is 28 February, not 3 March.
 *
 * This is what the header's chevrons move, and the reason they move a *date*
 * rather than a month is that the URL holds only `d`. The month on screen is
 * derived from it, so there is one fact and not two that can disagree. Keeping
 * the day of the month is what makes paging away and back land where you were,
 * which jumping to the 1st would not.
 *
 * Arithmetic rather than `Date.UTC`, which folds a two-digit year into the
 * 1900s — the strings here have already been round-tripped through
 * `toISOString`, so a four-digit year in the 0000–0099 range is a value this
 * can actually be handed.
 */
export function addMonths(date: string, months: number): string {
  const [year, month, day] = date.split("-").map(Number);
  const index = year * 12 + (month - 1) + months;
  const targetYear = Math.floor(index / 12);
  const targetMonth = index - targetYear * 12;
  const targetDay = Math.min(day, daysInMonth(targetYear, targetMonth));
  return `${pad(targetYear, 4)}-${pad(targetMonth + 1, 2)}-${pad(targetDay, 2)}`;
}

/** `monthIndex` is 0-11, as `Date#getUTCMonth` counts. */
function daysInMonth(year: number, monthIndex: number): number {
  const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  return [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][monthIndex];
}

/**
 * Not user-facing text, so not a job for `format.ts`: this builds the
 * machine-readable `YYYY-MM-DD` key the rest of the module speaks, the same
 * way `toISOString().slice(0, 10)` does above.
 */
const pad = (n: number, width: number) => String(n).padStart(width, "0");

/**
 * The forty-two dates on screen for the month `date` falls in, starting on
 * `weekStartsOn` (0 = Sunday, counting as `Date#getUTCDay` does).
 *
 * The leading and trailing cells are real days of the neighbouring months, not
 * blanks — they are dotted and selectable like any other, because a Monday at
 * the end of a week is the same Monday whichever month owns it.
 */
export function monthGrid(date: string, weekStartsOn: number): string[] {
  const first = `${monthOf(date)}-01`;
  const lead = (dateAsUtc(first).getUTCDay() - weekStartsOn + 7) % 7;
  const start = addDays(first, -lead);
  return Array.from({ length: GRID_DAYS }, (_, i) => addDays(start, i));
}

/**
 * The grid as instants, for `expandEvents` to be bounded by.
 *
 * From the first cell's midnight to the last cell's final millisecond, both
 * read in the household's zone — which is the zone the dots are a fact about,
 * since which day an event lands on is a question only a zone can answer.
 *
 * This replaces the forward-twelve-months window the scaffolding list used,
 * and replacing it is what brings the past back: navigating to March is what
 * expands March.
 */
export function monthWindow(grid: readonly string[], tz: string): Window {
  return {
    from: startOfDateIn(grid[0], tz),
    to: startOfDateIn(addDays(grid[grid.length - 1], 1), tz) - 1,
  };
}
